import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import Select from "react-select";
import { Link, useHistory } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  Table,
  Label,
  Form,
  Pagination,
  PaginationItem,
  Collapse,
  PaginationLink,
  Input,
} from "reactstrap";
import TicketListRow from "./TicketList";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import jwt_decode from "jwt-decode";
import axios from "../api/axios";
import { formatDuration } from "./ticketAge";
import "./tickets_list.css";

const CUSTOMER_SEARCH_URL = "/ticket/filter_tickets_customer";
const MANAGER_SEARCH_URL = "/ticket/filter_tickets_manager";
const COMPANY_SEARCH_URL = "/ticket/filter_tickets_company";
const EXCEL_DOWNLOAD_URL = "/ticket/download_excel";

// How many page buttons the pager shows at once.
const PAGE_WINDOW = 10;
// Must match PAGE_SIZES in Server/services/TicketList.js.
const PAGE_SIZES = [10, 25, 50];

// Header cards: stats key, label, icon, and the status filter a click applies ("" = all).
const STATUS_CARDS = [
  { key: "open", label: "Open", status: "Open", icon: "bx bx-file" },
  { key: "scheduled", label: "Scheduled", status: "Scheduled", icon: "bx bx-calendar" },
  { key: "inprogress", label: "In progress", status: "Inprogress", icon: "bx bx-cog" },
  { key: "unresolved", label: "Unresolved", status: "Unresolved", icon: "bx bx-error-circle" },
  { key: "completed", label: "Completed", status: "Completed", icon: "bx bx-check-circle" },
  { key: "total", label: "Total", status: "", icon: "bx bx-layer" },
];

const EMPTY_TIMING = { completedSampled: 0, medianHours: null, averageHours: null, within48Pct: null, over48: 0, over7d: 0 };

const STATUS_OPTIONS = ["Open", "Scheduled", "Inprogress", "Completed", "Unresolved"].map((s) => ({
  value: s,
  label: s,
}));

// Assignment filter: values are understood by the server's buildAssignmentClause.
const ASSIGNMENT_OPTIONS = [
  { value: "assigned", label: "Assigned" },
  { value: "unassigned", label: "Unassigned" },
  { value: "technicalStaff", label: "Maintenance Team" },
  { value: "vendor", label: "External Vendors" },
];

/** One object holds every filter control. Selects keep the react-select option, inputs keep strings. */
const EMPTY_FILTERS = {
  // Quick search box: request ID, suite, tenant, type or property.
  search: "",
  startDate: "",
  endDate: "",
  ticketID: "",
  status: "",
  assignment: "",
  property: null,
  propertyManager: null,
  vendor: null,
  technicalStaff: null,
  requestType: null,
  // Optional sub-type of the request type ("Fridge" under "Appliance").
  requestSubType: null,
  suite: "",
};

const BROWSER_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (e) {
    return "";
  }
})();

const personLabel = (p) =>
  [p?.first_name ?? p?.firstname, p?.last_name ?? p?.lastname].filter(Boolean).join(" ") ||
  p?.email ||
  "";

const TicketList = () => {
  const decode = jwt_decode(window.localStorage.getItem("accessToken"));
  const userRole = JSON.parse(window.localStorage.getItem("authUser"))?.role || decode.role;

  // Auditors are scoped to a company but their token id is their own auditor id,
  // so use company_id when a company-level id is expected.
  const companyScopeId = decode.role === "auditor" ? decode.company_id : decode.id;
  const isCompanyScope = !["customer", "manager"].includes(decode.role);

  const history = useHistory();

  const [col5, setcol5] = useState(false);
  const t_col5 = () => setcol5(!col5);

  // `draft` is what the panel shows; `applied` is what the list is actually
  // filtered by (only changes on Search / Clear). Keeping them separate means
  // typing in the panel never fires requests and pagination always uses the
  // filters that produced the current result set.
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(1);
  const [startPage, setStartPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  // Typed text; applied to the list after a short pause (see effect below).
  const [searchInput, setSearchInput] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [filteredTickets, setFilteredTickets] = useState([]);
  const [notesCount, setNotesCount] = useState({});
  const [totalTickets, setTotalTickets] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    scheduled: 0,
    inprogress: 0,
    completed: 0,
    unresolved: 0,
  });
  const [timing, setTiming] = useState(EMPTY_TIMING);

  // Dropdown data
  const [ticketPropertyList, setTicketPropertyList] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  // Every (requestType, requestSubType) pair that occurs in the viewer's scope,
  // from the server — the Sub-type filter offers only the chosen type's ones.
  const [requestSubTypes, setRequestSubTypes] = useState([]);
  const [propertyManagerList, setPropertyManagerList] = useState([]);
  const [comapnyTechnicalStaff, setComapnyTechnicalStaff] = useState([]);
  const [comapnyVendors, setComapnyVendors] = useState([]);

  const [url, setUrl] = useState("");
  const [copied, setcopied] = useState(false);

  // Cards ignore the Status filter; paging follows the rows actually listed.
  const pageTotal = Math.max(1, Math.ceil(totalTickets / pageSize));

  const setDraftField = (field) => (value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const handlePageChange = (page) => {
    if (page < 1 || page > pageTotal || page === pageNumber) return;
    setPageNumber(page);
    if (page > startPage + PAGE_WINDOW - 1 || page < startPage) {
      setStartPage(Math.max(1, Math.min(page - Math.floor(PAGE_WINDOW / 2), pageTotal - PAGE_WINDOW + 1)));
    }
  };

  /** Turn the applied filters into the request body every list/export endpoint understands. */
  const buildFilterBody = useCallback(
    (filters) => {
      const body = {
        search: (filters.search || "").trim(),
        startDate: filters.startDate || "",
        endDate: filters.endDate || "",
        timezone: BROWSER_TIMEZONE,
        status: filters.status || "",
        assignment: filters.assignment || "",
        propertyID: filters.property?.value || "",
        ticketID: (filters.ticketID || "").trim(),
        suite: (filters.suite || "").trim(),
        requestType: filters.requestType?.value || "",
        requestSubType: filters.requestSubType?.value || "",
      };
      if (decode.role === "customer") {
        body.customerID = decode.id;
      } else if (decode.role === "manager") {
        body.managerID = decode.id;
      } else {
        body.companyDomain = decode.domain;
        body.propertyManagerID = filters.propertyManager?.value || "";
        body.vendorID = filters.vendor?.value || "";
        body.technicalStaffID = filters.technicalStaff?.value || "";
      }
      return body;
    },
    [decode.role, decode.id, decode.domain]
  );

  const searchUrl =
    decode.role === "customer"
      ? CUSTOMER_SEARCH_URL
      : decode.role === "manager"
        ? MANAGER_SEARCH_URL
        : COMPANY_SEARCH_URL;

  // The one and only place the list is fetched. Runs whenever the applied
  // filters, the page, or the reload key change. Out-of-order responses are
  // dropped so a slow earlier request can never overwrite a newer result.
  const latestRequest = useRef(0);
  useEffect(() => {
    const requestId = ++latestRequest.current;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const response = await axios.post(searchUrl, {
          ...buildFilterBody(applied),
          pageNumber,
          pageSize,
        });
        if (cancelled || requestId !== latestRequest.current) return;

        const data = response.data || {};
        if (response.status !== 200 || data.status === 500 || data.success === false) {
          throw new Error(data.message || "Failed to load maintenance requests");
        }

        const ticketsArray = data.tickets || data.results?.tickets || [];
        setFilteredTickets(ticketsArray);
        setNotesCount(data.notesCount || {});
        setTotalTickets(data.totalCount ?? data.total?.Total ?? 0);
        if (Array.isArray(data.propertyList)) setTicketPropertyList(data.propertyList);
        if (Array.isArray(data.requestTypes)) setRequestTypes(data.requestTypes);
        if (Array.isArray(data.requestSubTypes)) setRequestSubTypes(data.requestSubTypes);
        setStats({
          total: data.total?.Total || 0,
          open: data.total?.Open?.count || 0,
          scheduled: data.total?.Scheduled?.count || 0,
          completed: data.total?.Completed?.count || 0,
          inprogress: data.total?.Inprogress?.count || 0,
          unresolved: data.total?.Unresolved?.count || 0,
        });
        setTiming({ ...EMPTY_TIMING, ...(data.timing || {}) });
      } catch (error) {
        if (cancelled || requestId !== latestRequest.current) return;
        console.error("Error searching tickets:", error);
        setLoadError(error?.response?.data?.message || error.message || "Failed to load maintenance requests");
      } finally {
        if (!cancelled && requestId === latestRequest.current) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [applied, pageNumber, pageSize, reloadKey, searchUrl, buildFilterBody]);

  /** Re-run the current search (same filters, same page). Used by row actions after they mutate a ticket. */
  const refreshTickets = useCallback(() => setReloadKey((k) => k + 1), []);

  const applyFilters = () => {
    if (draft.startDate && draft.endDate && draft.startDate > draft.endDate) {
      // Normalise a reversed range instead of silently returning nothing.
      const swapped = { ...draft, startDate: draft.endDate, endDate: draft.startDate };
      setDraft(swapped);
      setApplied(swapped);
    } else {
      setApplied({ ...draft });
    }
    setPageNumber(1);
    setStartPage(1);
  };

  /** Quick-bar controls apply at once (no Search click), and keep the panel in step. */
  const setQuickFilter = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setApplied((prev) => ({ ...prev, ...patch }));
    setPageNumber(1);
    setStartPage(1);
  };

  // Apply the search box 350 ms after typing stops.
  useEffect(() => {
    const term = searchInput.trim();
    if (term === (applied.search || "")) return undefined;
    const t = setTimeout(() => setQuickFilter({ search: term }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // "Copied" confirmation reverts to "Copy Link" after a moment.
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setcopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const ClearTicket = (e) => {
    e?.preventDefault?.();
    setSearchInput("");
    setDraft(EMPTY_FILTERS);
    setApplied({ ...EMPTY_FILTERS });
    setPageNumber(1);
    setStartPage(1);
    setcol5(false);
  };

  // Static dropdown data + share link. Independent of the list, so it loads in parallel.
  useEffect(() => {
    let active = true;
    const safe = (fn) => (res) => {
      if (active) fn(res);
    };
    const swallow = (label) => (err) => console.error(`Error loading ${label}:`, err);

    if (isCompanyScope) {
      axios
        .get("/vendor/get_vendors/" + companyScopeId)
        .then(safe((res) => res.data?.success && setComapnyVendors(res.data.vendors || [])))
        .catch(swallow("vendors"));
      axios
        .get("/technicalStaff/get_technical_staff/" + companyScopeId)
        .then(safe((res) => res.data?.success && setComapnyTechnicalStaff(res.data.technicalStaff || [])))
        .catch(swallow("technical staff"));
    }
    if (decode.role === "company") {
      axios
        .post(`/property_manager/admin`, { company: decode.id })
        .then(safe((res) => res.data?.managers && setPropertyManagerList(res.data.managers)))
        .catch(swallow("property managers"));
    }
    axios
      .get(`company/get-url/?company_id=${companyScopeId}&role=${decode.role}`)
      .then(
        safe((res) => {
          if (res.data?.status === 200) {
            setUrl(`${window.location.protocol}//${window.location.host}/maintenance-request/${res.data.data}`);
          }
        })
      )
      .catch(swallow("maintenance link"));

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadExcel = async () => {
    try {
      // Export exactly what the list is currently filtered by.
      const query = {
        ...buildFilterBody(applied),
        role: decode.role,
        id: decode.role === "auditor" ? decode.company_id : decode.id,
      };

      const response = await axios.post(EXCEL_DOWNLOAD_URL, query, {
        responseType: "blob",
        headers: {
          Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      if (blob.size < 100) throw new Error("Empty or invalid file");

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      link.setAttribute("download", `tickets_${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(blobUrl);
      link.remove();
    } catch (error) {
      console.error("Excel download failed:", error);
    }
  };

  // A status card applies (or, clicked again, clears) the status filter at once.
  const filterByStatus = (status) => {
    const next = (applied.status || "") === status ? "" : status;
    setDraft((prev) => ({ ...prev, status: next }));
    setApplied((prev) => ({ ...prev, status: next }));
    setPageNumber(1);
    setStartPage(1);
  };

  const propertyOptions = useMemo(
    () => ticketPropertyList.map((p) => ({ value: p._id, label: p.title })),
    [ticketPropertyList]
  );
  const requestTypeOptions = useMemo(() => requestTypes.map((t) => ({ value: t, label: t })), [requestTypes]);
  // With a request type chosen: that type's sub-types. Without one: every
  // sub-type in scope, labelled with its type so "Fridge" under two different
  // types cannot be confused. Empty → the Sub-type filter is not rendered.
  const requestSubTypeOptions = useMemo(() => {
    const chosenType = draft.requestType?.value || "";
    const pairs = chosenType
      ? requestSubTypes.filter((p) => p.requestType === chosenType)
      : requestSubTypes;
    const seen = new Set();
    return pairs
      .filter((p) => p.requestSubType && !seen.has(p.requestSubType) && seen.add(p.requestSubType))
      .map((p) => ({
        value: p.requestSubType,
        label: chosenType ? p.requestSubType : `${p.requestType} — ${p.requestSubType}`,
      }));
  }, [requestSubTypes, draft.requestType]);
  const managerOptions = useMemo(
    () => propertyManagerList.map((m) => ({ value: m._id, label: personLabel(m) })),
    [propertyManagerList]
  );
  const vendorOptions = useMemo(
    () =>
      comapnyVendors.map((v) => ({
        value: v._id,
        label: v.agency_name ? `${personLabel(v)} (${v.agency_name})` : personLabel(v),
      })),
    [comapnyVendors]
  );
  const staffOptions = useMemo(
    () => comapnyTechnicalStaff.map((t) => ({ value: t._id, label: personLabel(t) })),
    [comapnyTechnicalStaff]
  );

  const isSet = (v) => (typeof v === "string" ? v.trim() !== "" : v != null);
  const activeFilterCount = useMemo(() => Object.values(applied).filter(isSet).length, [applied]);
  // The Filters badge counts only what lives in the panel, not the quick bar.
  const panelFilterCount = useMemo(
    () =>
      Object.entries(applied).filter(
        ([k, v]) => !["search", "property", "status", "requestType"].includes(k) && isSet(v)
      ).length,
    [applied]
  );
  const firstShown = totalTickets ? (pageNumber - 1) * pageSize + 1 : 0;
  const lastShown = Math.min(pageNumber * pageSize, totalTickets);

  return (
    <React.Fragment>
      <div className="page-content mr-page">
        <MetaTags>
          <title>Rentdigicare | Maintenance Request</title>
        </MetaTags>

        <div className="container-fluid">
          <header className="mr-head">
            <div>
              <h1>Maintenance Request</h1>
              <p>Track and manage all maintenance requests and their status.</p>
            </div>
            <nav className="mr-crumbs" aria-label="breadcrumb">
              <Link to="/dashboard">Home</Link>
              <i className="bx bx-chevron-right" aria-hidden="true" />
              <span className="mr-crumbs__current" aria-current="page">Maintenance Request</span>
            </nav>
          </header>

          <section className="mr-panel mr-summary">
            <div className="mr-summary__body">
              <div className="tickets-status-cards" role="group" aria-label="Filter by status">
                {STATUS_CARDS.map(({ key, label, status, icon }) => {
                  // Total means "no status filter"; it is never shown as selected.
                  const active = Boolean(status) && applied.status === status;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`tickets-status-card tickets-status-card--${key}${active ? " is-active" : ""}`}
                      aria-pressed={active}
                      onClick={() => filterByStatus(status)}
                    >
                      <span className="tickets-status-card__icon" aria-hidden="true"><i className={icon} /></span>
                      <span className="tickets-status-card__count">{(stats[key] || 0).toLocaleString()}</span>
                      <span className="tickets-status-card__label">{label}</span>
                    </button>
                  );
                })}
              </div>

              <div
                className="tickets-insights"
                title="Created → closed, for completed tickets matching the current filters that have a recorded close time (recorded since Jan 2026)"
              >
                <div className="tickets-time">
                  <span className="tickets-time__label">Typical time to complete</span>{" "}
                  <span className="tickets-time__value">
                    {timing.medianHours == null ? "—" : formatDuration(timing.medianHours)}
                  </span>
                </div>
                <div className="tickets-time__sub">
                  {timing.completedSampled ? (
                    <>
                      average <b>{formatDuration(timing.averageHours)}</b> · <b>{timing.within48Pct}%</b> within 48 h · based on{" "}
                      {timing.completedSampled.toLocaleString()} completed
                    </>
                  ) : (
                    "No completed requests with a recorded close time in this view"
                  )}
                </div>
                <div className="tickets-overdue" aria-label="Overdue open requests">
                  <span className={`tickets-overdue__item tickets-overdue__item--amber${timing.over48 ? "" : " is-zero"}`}>
                    <strong>{timing.over48.toLocaleString()}</strong> open over 48 h
                  </span>
                  <span className="tickets-overdue__sep" aria-hidden="true">·</span>
                  <span className={`tickets-overdue__item tickets-overdue__item--red${timing.over7d ? "" : " is-zero"}`}>
                    <strong>{timing.over7d.toLocaleString()}</strong> over 7 days
                  </span>
                </div>
              </div>
            </div>

            <div className="mr-summary__actions">
              {!["vendor", "technical staff", "manager", "customer"].includes(decode.role) && (
                <CopyToClipboard text={url} onCopy={() => setcopied(true)}>
                  <button type="button" className="btn mr-btn-outline" disabled={!url} title="Copy the resident maintenance request link">
                    <i className={`bx ${copied ? "bx-check" : "bx-copy"} me-2`} aria-hidden="true" />
                    {copied ? "Link copied" : "Copy Maintenance Link"}
                  </button>
                </CopyToClipboard>
              )}
              {(decode.role === "manager" || decode.role === "customer") && (
                <button type="button" onClick={() => history.push("/ticket_manager")} className="btn mr-btn-outline">
                  <i className="bx bx-plus me-2" aria-hidden="true" />Add Maintenance Request
                </button>
              )}
              {decode.role !== "customer" && (
                <button type="button" onClick={downloadExcel} className="btn mr-btn-navy" title="Export the current view to Excel">
                  <i className="bx bx-download me-2" aria-hidden="true" />Export Excel
                </button>
              )}
              <button type="button" onClick={t_col5} className="btn mr-btn-blue" aria-expanded={col5}>
                <i className="bx bx-filter-alt me-2" aria-hidden="true" />Filters
                {panelFilterCount > 0 && <span className="mr-btn-badge">{panelFilterCount}</span>}
              </button>
            </div>
          </section>
          <section className="mr-panel mr-quickbar">
            <div className="mr-search">
              <i className="bx bx-search" aria-hidden="true" />
              <input
                type="search"
                className="form-control"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by request, unit, tenant…"
                aria-label="Search maintenance requests"
              />
            </div>
            <label className="mr-field">
              <span>Property</span>
              <select
                className="form-select"
                value={applied.property?.value || ""}
                onChange={(e) =>
                  setQuickFilter({ property: propertyOptions.find((o) => o.value === e.target.value) || null })
                }
              >
                <option value="">All Properties</option>
                {propertyOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="mr-field">
              <span>Status</span>
              <select
                className="form-select"
                value={applied.status || ""}
                onChange={(e) => setQuickFilter({ status: e.target.value })}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.value === "Inprogress" ? "In progress" : o.label}</option>
                ))}
              </select>
            </label>
            <label className="mr-field">
              <span>Request Type</span>
              <select
                className="form-select"
                value={applied.requestType?.value || ""}
                onChange={(e) =>
                  // A different type has different sub-types, so a chosen sub-type no longer applies.
                  setQuickFilter({
                    requestType: requestTypeOptions.find((o) => o.value === e.target.value) || null,
                    requestSubType: null,
                  })
                }
              >
                <option value="">All Types</option>
                {requestTypeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn-link mr-reset"
              onClick={ClearTicket}
              disabled={activeFilterCount === 0 && !searchInput}
            >
              <i className="bx bx-reset me-1" aria-hidden="true" />Reset
            </button>
          </section>

          <Collapse isOpen={col5}>
            <section className="mr-panel mr-advanced">
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  applyFilters();
                }}
              >
                <div className="row align-items-end">
                  <div className="col-md-3">
                    <Label>From</Label>
                    <Input
                      type="date"
                      value={draft.startDate}
                      max={draft.endDate || undefined}
                      onChange={(e) => setDraftField("startDate")(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <Label>To</Label>
                    <Input
                      type="date"
                      value={draft.endDate}
                      min={draft.startDate || undefined}
                      onChange={(e) => setDraftField("endDate")(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <Label>Request ID</Label>
                    <Input
                      type="text"
                      value={draft.ticketID}
                      onChange={(e) => setDraftField("ticketID")(e.target.value)}
                      placeholder="e.g. TIK1234"
                    />
                  </div>
                  {decode.role !== "customer" && (
                    <div className="col-md-3">
                      <Label>Assignment</Label>
                      <Select
                        value={ASSIGNMENT_OPTIONS.find((o) => o.value === draft.assignment) || null}
                        onChange={(opt) => setDraftField("assignment")(opt ? opt.value : "")}
                        options={ASSIGNMENT_OPTIONS}
                        isClearable
                        placeholder="Select..."
                      />
                    </div>
                  )}

                  {userRole === "company" && (
                    <>
                      <div className="col-md-3">
                        <Label>Property Manager</Label>
                        <Select
                          value={draft.propertyManager}
                          onChange={setDraftField("propertyManager")}
                          options={managerOptions}
                          isClearable
                          placeholder="Select..."
                        />
                      </div>
                      <div className="col-md-3">
                        <Label>Vendors</Label>
                        <Select
                          value={draft.vendor}
                          onChange={setDraftField("vendor")}
                          options={vendorOptions}
                          isClearable
                          placeholder="Select..."
                        />
                      </div>
                      <div className="col-md-3">
                        <Label>Technical Staff</Label>
                        <Select
                          value={draft.technicalStaff}
                          onChange={setDraftField("technicalStaff")}
                          options={staffOptions}
                          isClearable
                          placeholder="Select..."
                        />
                      </div>
                    </>
                  )}

                  {requestSubTypeOptions.length > 0 && (
                    <div className="col-md-3">
                      <Label>
                        {draft.requestType?.value ? `${draft.requestType.value} Type` : "Sub-type"}
                      </Label>
                      <Select
                        value={draft.requestSubType}
                        onChange={setDraftField("requestSubType")}
                        options={requestSubTypeOptions}
                        isClearable
                        placeholder="Any"
                      />
                    </div>
                  )}
                  <div className="col-md-3">
                    <Label>Suite</Label>
                    <Input
                      type="text"
                      value={draft.suite}
                      onChange={(e) => setDraftField("suite")(e.target.value)}
                      placeholder="Enter suite"
                    />
                  </div>

                  <div className="col-md-12 text-end mt-3">
                    <button type="submit" className="btn mr-btn-navy me-2" disabled={loading}>
                      {loading ? "Applying…" : "Apply filters"}
                    </button>
                    <button type="button" className="btn mr-btn-outline" onClick={ClearTicket}>
                      Clear
                    </button>
                  </div>
                </div>
              </Form>
            </section>
          </Collapse>

          <section className="mr-panel mr-table-panel">
            {loadError && (
              <div className="alert alert-danger py-2" role="alert">
                {loadError}
              </div>
            )}
            <div className="table-responsive tickets-table-wrap">
              <Table className="tickets-table mb-0">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Unit</th>
                    <th>Age</th>
                    <th>Status</th>
                    {decode.role !== "customer" && (
                      <>
                        <th>Workflow</th>
                        <th>Assigned</th>
                      </>
                    )}
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className={loading ? "is-loading" : undefined}>
                  {!loading && filteredTickets.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        {activeFilterCount > 0
                          ? "No maintenance requests match the selected filters."
                          : "No maintenance requests yet."}
                      </td>
                    </tr>
                  )}
                  <TicketListRow
                    filteredTickets={filteredTickets}
                    notesCount={notesCount}
                    searchTicket={refreshTickets}
                    getTicket={refreshTickets}
                    page={pageNumber}
                    pageSize={pageSize}
                    totalTickets={totalTickets}
                    setTotalTickets={setTotalTickets}
                  />
                </tbody>
              </Table>
            </div>

            <div className="mr-pager">
              <div className="mr-pager__count">
                Showing {firstShown.toLocaleString()} – {lastShown.toLocaleString()} of {totalTickets.toLocaleString()} requests
              </div>
              <Pagination className="mr-pager__pages" listClassName="mb-0">
                <PaginationItem disabled={pageNumber === 1}>
                  <PaginationLink first onClick={() => handlePageChange(1)} />
                </PaginationItem>
                <PaginationItem disabled={pageNumber === 1}>
                  <PaginationLink previous onClick={() => handlePageChange(pageNumber - 1)} />
                </PaginationItem>
                {Array.from({ length: PAGE_WINDOW }, (_, i) => startPage + i)
                  .filter((p) => p <= pageTotal)
                  .map((page) => (
                    <PaginationItem key={page} active={pageNumber === page}>
                      <PaginationLink onClick={() => handlePageChange(page)}>
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                <PaginationItem disabled={pageNumber >= pageTotal}>
                  <PaginationLink next onClick={() => handlePageChange(pageNumber + 1)} />
                </PaginationItem>
                <PaginationItem disabled={pageNumber >= pageTotal}>
                  <PaginationLink last onClick={() => handlePageChange(pageTotal)} />
                </PaginationItem>
              </Pagination>
              <label className="mr-pager__size">
                Rows per page
                <select
                  className="form-select form-select-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPageNumber(1);
                    setStartPage(1);
                  }}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>
        </div>
      </div>
    </React.Fragment>
  );
};

export default TicketList;
